import { z } from 'zod';
import { CUSTOM_FIELD_TYPES, ISSUE_TYPE_KEYS, RESOLUTION_CATEGORIES } from '../enums';
import { PROJECT_KEY_PATTERN } from '../utils';
import { uuid } from './common';

/** Project-level toggles (FR-PRJ-7, FR-TIME-8, FR-QT-5). */
export const projectSettingsSchema = z.object({
  timeTracking: z.boolean().default(true),
  timesheetApproval: z.boolean().default(false),
  testManagement: z.boolean().default(false),
  billableDefault: z.boolean().default(false),
  quickTaskEstimateCapMin: z.number().int().min(15).max(24 * 60).default(480),
  parentDoneRequiresChildrenDone: z.boolean().default(true),
  /** Pointing bugs is a project setting, default on (PRD §3.1). */
  storyPointsOnBugs: z.boolean().default(true),
  /** Status reached when a retest fully passes (Phase 2, FR-TST-15); never a hard jump to Done. */
  verifiedStatusKey: z.string().default('awaiting_approval'),
  /** Phase 2 (FR-REL-8): promote needs an approved go/no-go for that build. */
  requireGoNoGo: z.boolean().default(true),
  /** Documents every release needs (FR-DOC-15). */
  releaseDocTypes: z.array(z.string()).default(['RELEASE_NOTES', 'USER_GUIDE', 'CONFIG_GUIDE']),
  isInternal: z.boolean().default(false),
  builtinRouting: z.boolean().default(true),
  aiEnabled: z.boolean().default(true),
});
export type ProjectSettings = z.infer<typeof projectSettingsSchema>;
export const DEFAULT_PROJECT_SETTINGS: ProjectSettings = projectSettingsSchema.parse({});

export const createProjectSchema = z.object({
  name: z.string().min(2).max(120),
  key: z.string().regex(PROJECT_KEY_PATTERN, '2–10 uppercase letters/digits, starting with a letter'),
  description: z.string().max(2000).optional(),
  leadId: uuid.optional(),
  triageOwnerId: uuid.optional(),
  settings: projectSettingsSchema.partial().optional(),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  leadId: uuid.nullable().optional(),
  triageOwnerId: uuid.nullable().optional(),
  settings: projectSettingsSchema.partial().optional(),
  archived: z.boolean().optional(),
});
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

export const componentSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(1000).nullable().optional(),
  defaultDevOwnerId: uuid.nullable().optional(),
  defaultQaOwnerId: uuid.nullable().optional(),
});
export type ComponentInput = z.infer<typeof componentSchema>;

export const labelSchema = z.object({
  name: z.string().min(1).max(60),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#64748b'),
});
export type LabelInput = z.infer<typeof labelSchema>;

export const issueTypeSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/),
  name: z.string().min(1).max(60),
  hierarchyLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  icon: z.string().max(40).default('square'),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#3b82f6'),
  workflowId: uuid.optional(),
});
export type IssueTypeInput = z.infer<typeof issueTypeSchema>;
export const BUILTIN_ISSUE_TYPES = ISSUE_TYPE_KEYS;

export const customFieldDefSchema = z
  .object({
    fieldKey: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
    label: z.string().min(1).max(80),
    dataType: z.enum(CUSTOM_FIELD_TYPES),
    options: z.array(z.string().min(1).max(80)).optional(),
    required: z.boolean().default(false),
    appliesToIssueTypes: z.array(z.string()).default([]),
  })
  .refine((v) => !['select', 'multi_select'].includes(v.dataType) || (v.options && v.options.length > 0), {
    message: 'Select fields require options',
    path: ['options'],
  });
export type CustomFieldDefInput = z.infer<typeof customFieldDefSchema>;

export const resolutionSchema = z.object({
  name: z.string().min(1).max(60),
  category: z.enum(RESOLUTION_CATEGORIES),
});
export type ResolutionInput = z.infer<typeof resolutionSchema>;
